import { BuySeatsDialog } from "@/components/common/BuySeatsDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { InviteDialog } from "@/components/common/InviteDialog";
import { RoleBadge, SeatStatusBadge } from "@/components/common/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useMemberMutations, useMembers, useOrg } from "@/hooks";
import { ApiError } from "@/services";
import type { InviteResult, OrgMember } from "@/types/api";
import { useState } from "react";

export function SeatsPage() {
	const org = useOrg();
	const members = useMembers();
	const { invite, deactivate, setRole } = useMemberMutations();
	const [raw, setRaw] = useState("");
	const [result, setResult] = useState<InviteResult | null>(null);
	// Invited, but not added to the chosen team. Reported beside every other
	// per-email outcome rather than inside the dialog that produced it.
	const [notAssigned, setNotAssigned] = useState<string[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [pendingRemoval, setPendingRemoval] = useState<OrgMember | null>(null);
	const [buying, setBuying] = useState<{
		seats: number;
		emails: string[];
	} | null>(null);
	// The addresses the confirm dialog is currently about. Held separately from
	// `raw` so clearing the box on success cannot empty the dialog mid-flight.
	const [confirming, setConfirming] = useState<string[] | null>(null);

	const seats = org.data?.seats;
	const rows = members.data?.members ?? [];
	// Only an owner may change roles; the server enforces it, this just avoids
	// showing a control that would always be refused.
	const isOwner = org.data?.org.role === "owner";

	// Paste-a-list is the real onboarding motion, so accept whatever separators
	// come out of a spreadsheet or an email.
	const emails = raw
		.split(/[\s,;]+/)
		.map((e) => e.trim())
		.filter(Boolean);

	/**
	 * Confirm before sending, so the same step can put these people on a team.
	 * That is only possible while the admin still knows who they just invited —
	 * afterwards it means finding each new name in a roster that now contains
	 * them.
	 */
	function onInvite() {
		setError(null);
		setResult(null);
		setNotAssigned([]);
		setConfirming(emails);
	}

	function onInvited(r: InviteResult, couldNotAssign: string[]) {
		setResult(r);
		setNotAssigned(couldNotAssign);
		setRaw("");
		// Hitting the cap is not an error to report and walk away from — it is a
		// purchase the admin can make right now, for a known number of seats and
		// a known set of people. Offer it immediately.
		if (r.over_capacity.length > 0) {
			setBuying({ seats: r.over_capacity.length, emails: r.over_capacity });
		}
	}

	return (
		<>
			<PageHeader
				title="Seats"
				description="Invite your team and manage who holds a seat."
			/>

			{seats ? (
				<Card className="mb-6">
					<CardContent className="flex items-center gap-8 p-5">
						<Stat label="Active" value={seats.active} />
						<Stat label="Invited" value={seats.invited} />
						{/* null purchased = unlimited. Rendering it as a number would
						    show "0 purchased / 0 available" for a contract that
						    covers the whole company. */}
						<Stat label="Purchased" value={seats.purchased ?? "Unlimited"} />
						{seats.available != null ? (
							<Stat
								label="Available"
								value={seats.available}
								tone={seats.available === 0 ? "critical" : "default"}
							/>
						) : null}
						{/* Unlimited plans have nothing to buy, so no button at all. */}
						{seats.purchased != null ? (
							<Button
								variant="secondary"
								size="sm"
								className="ml-auto"
								onClick={() => setBuying({ seats: 1, emails: [] })}
							>
								Add seats
							</Button>
						) : null}
					</CardContent>
				</Card>
			) : (
				<Skeleton className="mb-6 h-24 w-full" />
			)}

			<Card className="mb-6">
				<CardContent className="p-5">
					<label
						htmlFor="invite"
						className="text-body font-medium text-heading"
					>
						Invite people
					</label>
					<p className="mb-3 mt-1 text-caption text-muted-foreground">
						Paste any number of email addresses, separated by spaces, commas or
						new lines.
					</p>
					<div className="flex gap-2">
						<Input
							id="invite"
							value={raw}
							onChange={(e) => setRaw(e.target.value)}
							placeholder="ada@example.com, grace@example.com"
						/>
						<Button onClick={onInvite} disabled={emails.length === 0}>
							{`Invite${emails.length > 1 ? ` ${emails.length}` : ""}`}
						</Button>
					</div>

					{error ? (
						<p className="mt-3 text-caption text-error">{error}</p>
					) : null}
					{result ? (
						<InviteSummary result={result} notAssigned={notAssigned} />
					) : null}
				</CardContent>
			</Card>

			{members.isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : rows.length === 0 ? (
				<EmptyState
					title="No one here yet"
					description="Invite your team above. They'll get a seat the first time they sign in to Curo with that address."
				/>
			) : (
				<Card>
					<div className="overflow-x-auto">
						<table className="w-full text-body">
							<thead>
								<tr className="border-b border-divider text-caption uppercase tracking-wide text-muted-foreground">
									<Th>Person</Th>
									<Th>Status</Th>
									<Th>Joined</Th>
									<Th>Last active</Th>
									<Th />
								</tr>
							</thead>
							<tbody>
								{rows.map((m) => (
									<tr
										key={m.id}
										className="border-b border-divider last:border-0"
									>
										<Td>
											<div className="flex items-center gap-2">
												<div className="min-w-0">
													<div className="truncate font-medium text-heading">
														{m.user_name || m.email}
													</div>
													{m.user_name ? (
														<div className="truncate text-caption text-muted-foreground">
															{m.email}
														</div>
													) : null}
												</div>
												<RoleBadge role={m.role} />
											</div>
										</Td>
										<Td>
											<SeatStatusBadge status={m.seat_status} />
										</Td>
										<Td className="tabular text-muted-foreground">
											{fmt(m.activated_at)}
										</Td>
										<Td className="tabular text-muted-foreground">
											{fmt(m.last_active_at)}
										</Td>
										<Td className="text-right">
											<div className="flex items-center justify-end gap-1">
												{/*
												 * Role changes are owner-only server-side, so an
												 * admin is shown nothing rather than a control
												 * that always fails. The last-owner guard also
												 * lives on the server — this just avoids
												 * offering the move that it will refuse.
												 */}
												{isOwner && m.seat_status === "active" ? (
													<select
														value={m.role}
														disabled={setRole.isPending}
														onChange={(e) =>
															setRole.mutate({
																memberId: m.id,
																role: e.target.value as OrgMember["role"],
															})
														}
														className="rounded-md border border-divider bg-surface-primary px-2 py-1 text-caption text-paragraph focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
														aria-label={`Role for ${m.email}`}
													>
														<option value="member">Member</option>
														<option value="admin">Admin</option>
														<option value="owner">Owner</option>
													</select>
												) : null}
												{m.seat_status !== "deactivated" ? (
													<Button
														variant="ghost"
														size="sm"
														disabled={deactivate.isPending}
														onClick={() => setPendingRemoval(m)}
													>
														Remove
													</Button>
												) : null}
											</div>
										</Td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Card>
			)}

			{/*
			 * Confirmation states the real consequence rather than asking "are you
			 * sure" — the two things a person actually wants to know before
			 * removing a colleague are whether the seat comes back and whether
			 * their work is destroyed.
			 */}
			<AlertDialog
				open={pendingRemoval !== null}
				onOpenChange={(open) => !open && setPendingRemoval(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							Remove {pendingRemoval?.user_name || pendingRemoval?.email}?
						</AlertDialogTitle>
						<AlertDialogDescription>
							Their seat is freed straight away and they lose access to{" "}
							{org.data?.org.name}.{" "}
							<span className="text-heading">
								Nothing they have learned is deleted
							</span>{" "}
							— you can invite them back at any time.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep them</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={() => {
								if (pendingRemoval) deactivate.mutate(pendingRemoval.id);
								setPendingRemoval(null);
							}}
						>
							Remove
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<InviteDialog
				open={confirming !== null}
				onOpenChange={(o) => !o && setConfirming(null)}
				emails={confirming ?? []}
				onInvited={onInvited}
			/>

			<BuySeatsDialog
				open={buying !== null}
				onOpenChange={(o) => !o && setBuying(null)}
				defaultAdditional={buying?.seats ?? 1}
				blockedEmails={buying?.emails}
				cycle={org.data?.plan?.billing_cycle}
				currentSeats={seats?.purchased ?? null}
				onPurchased={async () => {
					// The seats exist now, so the invites that bounced will land.
					if (!buying?.emails.length) return;
					try {
						setResult(await invite.mutateAsync({ emails: buying.emails }));
					} catch (e) {
						// Money has already changed hands at this point. Failing
						// silently here leaves an admin who bought seats and got
						// no invites, with nothing on screen to say so.
						setError(
							e instanceof ApiError
								? e.message
								: "Seats were added, but the invites did not send. Try inviting again.",
						);
					}
				}}
			/>
		</>
	);
}

function InviteSummary({
	result,
	notAssigned,
}: { result: InviteResult; notAssigned: string[] }) {
	// Per-email outcomes, because "something went wrong" for fifty addresses is
	// useless to whoever pasted them.
	const lines: { tone: string; text: string }[] = [];
	if (result.invited.length)
		lines.push({
			tone: "text-success",
			text: `${result.invited.length} invited`,
		});
	if (result.already_member.length)
		lines.push({
			tone: "text-muted-foreground",
			text: `${result.already_member.length} already on the team: ${result.already_member.join(", ")}`,
		});
	if (result.invalid.length)
		lines.push({
			tone: "text-error",
			text: `${result.invalid.length} not a valid address: ${result.invalid.join(", ")}`,
		});
	if (result.over_capacity.length)
		lines.push({
			tone: "text-orange-text",
			text: `${result.over_capacity.length} couldn't be invited — no seats left: ${result.over_capacity.join(", ")}`,
		});
	if (notAssigned.length)
		// They ARE invited; only the team assignment did not take. Saying
		// otherwise would send the admin chasing the wrong problem.
		lines.push({
			tone: "text-orange-text",
			text: `${notAssigned.length} invited, but not added to the team: ${notAssigned.join(", ")}. Add them from Teams.`,
		});

	return (
		<div className="mt-3 flex flex-col gap-1">
			{lines.map((l) => (
				<p key={l.text} className={`text-caption ${l.tone}`}>
					{l.text}
				</p>
			))}
		</div>
	);
}

function Stat({
	label,
	value,
	tone = "default",
}: { label: string; value: number | string; tone?: "default" | "critical" }) {
	return (
		<div>
			<div
				className={`tabular text-h5 font-semibold ${tone === "critical" ? "text-error" : "text-heading"}`}
			>
				{value}
			</div>
			<div className="text-caption text-muted-foreground">{label}</div>
		</div>
	);
}

const Th = ({ children }: { children?: React.ReactNode }) => (
	<th className="px-5 py-2.5 text-left font-medium">{children}</th>
);
const Td = ({
	children,
	className = "",
}: { children?: React.ReactNode; className?: string }) => (
	<td className={`px-5 py-3 ${className}`}>{children}</td>
);

const fmt = (d: string | null) =>
	d
		? new Date(d).toLocaleDateString(undefined, {
				day: "numeric",
				month: "short",
			})
		: "—";
